import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { finApi, finErr, money, LEDGER_BUCKETS, BUCKET_LABEL } from "@/lib/financeApi";

const PAGE_SIZE = 25;
const field = "[color-scheme:dark] bg-black/40 border border-white/10 focus:border-nex-gold/60 outline-none text-nex-silver font-mono text-[11px] px-2 py-1.5 rounded-sm";

function Row({ t, lit }) {
  const credit = t.direction === "credit";
  return (
    <tr data-testid={`fin-tx-row-${t.id}`} data-highlighted={lit ? "true" : undefined} className={`border-t border-white/5 ${lit ? "bg-nex-gold/15 outline outline-1 outline-nex-gold/60" : ""}`}>
      <td className="px-3 py-2 font-mono text-[10px] text-nex-muted whitespace-nowrap">{new Date(t.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</td>
      <td className="px-3 py-2 text-xs text-nex-silver capitalize">{t.type}</td>
      <td className="px-3 py-2 font-mono text-[10px] uppercase text-nex-muted">{BUCKET_LABEL[t.bucket] || t.bucket}</td>
      <td className="px-3 py-2 text-xs text-nex-muted max-w-[220px] truncate" title={t.description}>{t.description || "—"}</td>
      <td className={`px-3 py-2 font-mono text-xs text-right ${credit ? "text-emerald-300" : "text-red-300"}`}>{credit ? "+" : "−"}{money(t.amount)}</td>
      <td className="px-3 py-2 font-mono text-xs text-right text-nex-silver">{money(t.balance_after)}</td>
    </tr>
  );
}

export default function LedgerTab({ resident, highlight }) {
  const day = highlight?.created_at?.slice(0, 10) || "";
  const [f, setF] = useState({ bucket: highlight?.bucket || "", date_from: day, date_to: day });
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, items: [], total: 0, error: null });
  const [miss, setMiss] = useState(false);
  const done = useRef(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    const params = { page, page_size: PAGE_SIZE, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    finApi.get(`/residents/${resident.id}/ledger`, params)
      .then((d) => setState({ loading: false, items: d.items, total: d.total, error: null }))
      .catch((e) => setState((s) => ({ ...s, loading: false, error: finErr(e) })));
  }, [resident.id, f, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!highlight || done.current || state.loading) return;
    done.current = true;
    const el = document.querySelector(`[data-testid="fin-tx-row-${highlight.txn_id}"]`);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" }); else setMiss(true);
  }, [highlight, state.loading]);

  const set = (k) => (e) => { setPage(1); setF((x) => ({ ...x, [k]: e.target.value })); };
  const pages = Math.max(1, Math.ceil(state.total / PAGE_SIZE));

  return (
    <div data-testid="fin-ledger-tab" className="p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select value={f.bucket} onChange={set("bucket")} data-testid="fin-ledger-filter-bucket" aria-label="Bucket" className={field}>
          <option value="">All buckets</option>
          {LEDGER_BUCKETS.map((b) => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
        </select>
        <span data-testid="fin-ledger-total" className="ml-auto font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">{state.total} rows</span>
      </div>
      {state.error && <p data-testid="fin-ledger-tab-error" className="text-red-300 text-sm">{state.error}</p>}
      {miss && <p data-testid="fin-ledger-notice" className="text-amber-200 text-xs">That entry is no longer in view</p>}
      <div className="border border-white/10 rounded-sm overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
            {["Date", "Type", "Bucket", "Description", "Amount", "Balance after"].map((h, i) => <th key={h} className={`px-3 py-2 font-normal ${i > 3 ? "text-right" : ""}`}>{h}</th>)}
          </tr></thead>
          <tbody>
            {state.loading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-nex-muted text-xs"><Loader2 className="inline h-4 w-4 animate-spin" /></td></tr>
            ) : state.items.length ? state.items.map((t) => <Row key={t.id} t={t} lit={highlight?.txn_id === t.id} />) : (
              <tr><td colSpan={6} data-testid="fin-ledger-tab-empty" className="px-3 py-8 text-center text-nex-muted text-xs">No transactions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-2 font-mono text-[10px] tracking-[0.2em] uppercase">
        <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="fin-ledger-prev" className="px-3 py-1 border border-white/10 rounded-sm text-nex-muted enabled:hover:text-nex-gold disabled:opacity-40">Prev</button>
        <span data-testid="fin-ledger-page" className="text-nex-muted">Page {page} / {pages}</span>
        <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="fin-ledger-next" className="px-3 py-1 border border-white/10 rounded-sm text-nex-muted enabled:hover:text-nex-gold disabled:opacity-40">Next</button>
      </div>
    </div>
  );
}
